import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  InputBase,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface TopBarProps {
  onMenuClick?: () => void;
  sidebarCollapsed?: boolean;
  isMobile?: boolean;
  breadcrumbs?: BreadcrumbItem[];
}

// Styled search component
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.2),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.3),
  },
  width: '100%',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

const TopBar: React.FC<TopBarProps> = ({ 
  onMenuClick, 
  sidebarCollapsed = false,
  isMobile = false,
  breadcrumbs = []
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  const handleBreadcrumbClick = (breadcrumb: BreadcrumbItem) => {
    if (breadcrumb.path) {
      navigate(breadcrumb.path);
    }
  };


  const menuId = 'primary-search-account-menu';

  return (
    <AppBar 
      position="fixed" 
      sx={{
        width: { 
          md: isMobile 
            ? '100%' 
            : sidebarCollapsed 
              ? 'calc(100% - 64px)' 
              : 'calc(100% - 240px)' 
        },
        ml: { 
          md: isMobile 
            ? 0 
            : sidebarCollapsed 
              ? '64px' 
              : '240px' 
        },
        backgroundColor: (theme) => theme.palette.primary.light,
        color: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin-left'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <Toolbar sx={{ minHeight: '64px !important' }}>
        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Breadcrumbs - Only show when there are breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'column',
            alignItems: 'flex-start',
            minWidth: 'fit-content'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              {breadcrumbs.map((breadcrumb, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: index === breadcrumbs.length - 1 ? 'white' : 'rgba(255, 255, 255, 0.7)',
                      //color: '#000000',
                      fontWeight: index === breadcrumbs.length - 1 ? 400 : 400,
                      fontSize: '1rem',
                      cursor: breadcrumb.path ? 'pointer' : 'default',
                      '&:hover': breadcrumb.path ? {
                        textDecoration: 'underline'
                      } : {}
                    }}
                    onClick={() => handleBreadcrumbClick(breadcrumb)}
                  >
                    {breadcrumb.label}
                  </Typography>
                  {index < breadcrumbs.length - 1 && (
                    <NavigateNextIcon 
                      sx={{ 
                        fontSize: '1rem', 
                        color: 'rgba(255, 255, 255, 0.5)',
                        mx: 0.5
                      }} 
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Centered Search Bar */}
        <Box sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          justifyContent: 'center',
          px: 2
        }}>
          <Search sx={{ 
            maxWidth: 400,
            width: '100%',
            mx: 'auto'
          }}>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search..."
              inputProps={{ 'aria-label': 'search' }}
            />
          </Search>
        </Box>


        {/* User Profile */}
        <IconButton
          size="large"
          edge="end"
          aria-label="account of current user"
          aria-controls={menuId}
          aria-haspopup="true"
          onClick={handleProfileMenuOpen}
          color="inherit"
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
            <AccountCircleIcon />
          </Avatar>
        </IconButton>


        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          id={menuId}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 250,
            }
          }}
        >
          <Box sx={{ px: 2, py: 2, backgroundColor: (theme) => theme.palette.background.default }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: (theme) => theme.palette.primary.light }}>
                <AccountCircleIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {user?.email || 'User'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Administrator
                </Typography>
              </Box>
            </Box>
          </Box>
          {/* <Divider />
          <MenuItem onClick={handleMenuClose}>
            <AccountCircleIcon sx={{ mr: 2 }} />
            Profile
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>
            <SettingsIcon sx={{ mr: 2 }} />
            Settings
          </MenuItem> */}
          <Divider />
          <MenuItem onClick={handleLogout}>
            <LogoutIcon sx={{ mr: 2, mt: 1 }} />
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
